import * as Cesium from "cesium";
import { markRaw } from "vue";
import { toCesiumDegrees } from "@/utils/coordTransform";
import {
  resolvePointIconUrl,
  makeBillboardOptions,
  makePointLabelOptions,
} from "@/cesium/pointIcons";

/** 接口坐标默认 GCJ-02，上 Cesium（WGS84）前转换 */
const DATA_CRS = "gcj02";

/**
 * 图层数据转换版本：变更后强制丢弃缓存并重新加载
 * （避免 HMR 后仍显示未转换的旧圩区/堤防）
 */
export const LAYER_DATA_VERSION = 27;

/**
 * 从 src/resData 按需加载样例 JSON（webpack 动态 import，按文件分包）
 * @param {string} file 如 "polder.json"
 */
export async function loadApiJson(file) {
  const name = String(file).replace(/\.json$/i, "");
  const mod = await import(
    /* webpackChunkName: "resData-[request]" */
    /* webpackMode: "lazy" */
    `../resData/${name}.json`
  );
  return mod.default ?? mod;
}

/** @deprecated 使用 loadApiJson；保留别名避免外部旧引用 */
export const apiUrl = loadApiJson;

/** 解析 "lng,lat" 或 "lng,lat;lng,lat;..."（原始业务坐标，未转换） */
export function parseLonLat(str) {
  if (!str || typeof str !== "string") return null;
  const parts = str.trim().split(",");
  if (parts.length < 2) return null;
  const lng = Number(parts[0]);
  const lat = Number(parts[1]);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { lng, lat };
}

export function parseLonLatChain(str) {
  if (!str || typeof str !== "string") return [];
  return str
    .split(";")
    .map((s) => parseLonLat(s))
    .filter(Boolean);
}

/** 业务坐标 → Cesium WGS84 */
function toWgs(lng, lat, fromCrs = DATA_CRS) {
  return toCesiumDegrees(lng, lat, fromCrs);
}

function rgbaToColor(rgba, fallbackHex = "#3de7ff") {
  if (!rgba || typeof rgba !== "string") {
    return Cesium.Color.fromCssColorString(fallbackHex);
  }
  const m = rgba.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i
  );
  if (!m) return Cesium.Color.fromCssColorString(fallbackHex);
  const r = Number(m[1]) / 255;
  const g = Number(m[2]) / 255;
  const b = Number(m[3]) / 255;
  const a = m[4] != null ? Number(m[4]) : 1;
  return new Cesium.Color(r, g, b, a);
}

/** 聚合点：青蓝科技环，数量越大环越大、色越暖 */
const clusterIconCache = new Map();
function getClusterLevel(count) {
  if (count < 10) return { key: "s", size: 44, fill: "#0a3d52", stroke: "#3de7ff", glow: "rgba(61,231,255,0.55)" };
  if (count < 50) return { key: "m", size: 52, fill: "#0a3558", stroke: "#42a5f5", glow: "rgba(66,165,245,0.55)" };
  if (count < 200) return { key: "l", size: 60, fill: "#2a2048", stroke: "#ab47bc", glow: "rgba(171,71,188,0.5)" };
  return { key: "xl", size: 68, fill: "#4a1820", stroke: "#ff7043", glow: "rgba(255,112,67,0.55)" };
}

function getClusterIcon(count) {
  const { key, size, fill, stroke, glow } = getClusterLevel(count);
  const cacheKey = `${key}-${count}`;
  if (clusterIconCache.has(cacheKey)) return clusterIconCache.get(cacheKey);

  const dpr = 2;
  const canvas = document.createElement("canvas");
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 3;

  // 外圈光晕
  const grad = ctx.createRadialGradient(cx, cy, r * 0.35, cx, cy, r + 2);
  grad.addColorStop(0, glow);
  grad.addColorStop(1, "rgba(0,0,0,0)");
  ctx.beginPath();
  ctx.arc(cx, cy, r + 2, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();

  // 实心底
  ctx.beginPath();
  ctx.arc(cx, cy, r - 1, 0, Math.PI * 2);
  ctx.fillStyle = fill;
  ctx.globalAlpha = 0.92;
  ctx.fill();
  ctx.globalAlpha = 1;

  // 外环
  ctx.beginPath();
  ctx.arc(cx, cy, r - 1, 0, Math.PI * 2);
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // 内环虚感
  ctx.beginPath();
  ctx.arc(cx, cy, r - 6, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(255,255,255,0.28)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // 数字
  const text = count > 999 ? "999+" : String(count);
  ctx.fillStyle = "#ffffff";
  ctx.font = `bold ${count > 99 ? 12 : 14}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.65)";
  ctx.shadowBlur = 3;
  ctx.fillText(text, cx, cy + 0.5);

  // 直接缓存 canvas，避免 dataURL 异步贴图触发销毁中的 VertexArray
  clusterIconCache.set(cacheKey, canvas);
  return canvas;
}

/**
 * 点位聚合（须在 dataSources.add 之后调用）
 * 默认关闭：缩放时 cluster 重建会销毁半透明 Billboard VAO → VertexArray destroyed
 */
function enablePointClustering(dataSource, opts = {}) {
  const clustering = dataSource.clustering;
  clustering.enabled = true;
  clustering.pixelRange = opts.pixelRange ?? 56;
  clustering.minimumClusterSize = opts.minimumClusterSize ?? 3;
  clustering.clusterBillboards = true;
  clustering.clusterLabels = true;
  clustering.clusterPoints = true;

  clustering.clusterEvent.addEventListener((clusteredEntities, cluster) => {
    try {
      if (!cluster?.billboard) return;
      const n = clusteredEntities.length;
      const { size } = getClusterLevel(n);
      cluster.label.show = false;
      cluster.billboard.show = true;
      cluster.billboard.image = getClusterIcon(n);
      cluster.billboard.width = size;
      cluster.billboard.height = size;
      cluster.billboard.verticalOrigin = Cesium.VerticalOrigin.CENTER;
      cluster.billboard.horizontalOrigin = Cesium.HorizontalOrigin.CENTER;
      cluster.billboard.disableDepthTestDistance = Number.POSITIVE_INFINITY;
      cluster.billboard.scale = 1;
      if (cluster.point) cluster.point.show = false;
    } catch (_) {
      /* 聚合重建过程中 billboard 可能已销毁，忽略 */
    }
  });
}

async function fetchJson(file) {
  try {
    return await loadApiJson(file);
  } catch (e) {
    throw new Error(`加载失败 ${file}: ${e?.message || e}`);
  }
}

/**
 * 图层配置：按业务分组，默认开启关键概览层，管网按需/主干
 */
export const LAYER_DEFS = [
  {
    key: "polder",
    name: "圩区范围",
    group: "水利工程",
    colorHex: "#00e5ff",
    defaultVisible: true,
    kind: "polder",
    file: "polder.json",
  },
  {
    key: "dike",
    name: "堤防",
    group: "水利工程",
    colorHex: "#42a5f5",
    defaultVisible: true,
    kind: "dike",
    file: "dike.json",
  },
  {
    key: "gate",
    name: "闸站",
    group: "水利工程",
    colorHex: "#4fc3f7",
    defaultVisible: true,
    kind: "listPoint",
    file: "gate.json",
    listGeom: "gategeom",
    listName: "gatename",
    icon: "闸门",
    iconSize: 42,
  },
  {
    key: "warehouse",
    name: "防汛仓库",
    group: "防汛资源",
    colorHex: "#ffd54f",
    defaultVisible: true,
    kind: "listPoint",
    file: "warehouse.json",
    listGeom: "longitude",
    listName: "cbdname",
    icon: "控制柜",
    iconSize: 42,
  },
  {
    key: "shelter",
    name: "避难场所",
    group: "防汛资源",
    colorHex: "#81c784",
    defaultVisible: true,
    kind: "listPoint",
    file: "shelter.json",
    listGeom: "longitude",
    listName: "name",
    icon: "控制柜",
    iconSize: 42,
  },
  {
    key: "rescue",
    name: "抢险队伍",
    group: "防汛资源",
    colorHex: "#ef5350",
    defaultVisible: true,
    kind: "listPoint",
    file: "rescue.json",
    listGeom: "longitude",
    listName: "rname",
    icon: "控制柜",
    iconSize: 42,
  },
  {
    key: "drainPump",
    name: "排水泵站",
    group: "排水监测",
    colorHex: "#26c6da",
    defaultVisible: true,
    kind: "monitorPoint",
    file: "drain-pump.json",
    icon: "排水泵",
    iconSize: 42,
  },
  {
    key: "agriPump",
    name: "农污泵站",
    group: "排水监测",
    colorHex: "#66bb6a",
    defaultVisible: true,
    kind: "monitorPoint",
    file: "agri-pump.json",
    icon: "潜污泵",
    iconSize: 42,
  },
  {
    key: "wwtp",
    name: "污水处理厂",
    group: "排水监测",
    colorHex: "#ab47bc",
    defaultVisible: true,
    kind: "monitorPoint",
    file: "wwtp.json",
    icon: "鼓风机",
    iconSize: 42,
  },
  {
    key: "supplyPump",
    name: "二供泵房",
    group: "供水监测",
    colorHex: "#29b6f6",
    defaultVisible: true,
    kind: "monitorPoint",
    file: "supply-pump.json",
    icon: "取水泵",
    iconSize: 42,
  },
  {
    key: "supplyPressure",
    name: "管网压力",
    group: "供水监测",
    colorHex: "#5c6bc0",
    defaultVisible: true,
    kind: "monitorPoint",
    file: "supply-pressure.json",
    icon: "流量计",
    iconSize: 42,
  },
  {
    key: "drainUser",
    name: "排水户",
    group: "排水监测",
    colorHex: "#78909c",
    defaultVisible: false,
    kind: "monitorPoint",
    file: "drain-user.json",
    icon: "垃圾收集器",
    iconSize: 42,
  },
  {
    key: "sq",
    name: "水情水位",
    group: "监测站点",
    colorHex: "#40c4ff",
    defaultVisible: false,
    kind: "monitorPoint",
    file: "water-level.json",
    icon: "液位计",
    iconSize: 42,
  },
  {
    key: "yq",
    name: "雨情",
    group: "监测站点",
    colorHex: "#7e57c2",
    defaultVisible: false,
    kind: "monitorPoint",
    file: "rainfall.json",
    icon: "流量计",
    iconSize: 42,
  },
  {
    key: "jsjc",
    name: "积水监测",
    group: "监测站点",
    colorHex: "#ff7043",
    defaultVisible: false,
    kind: "monitorPoint",
    file: "ponding.json",
    icon: "液位计",
    iconSize: 42,
  },
  {
    key: "gq",
    name: "闸门工情",
    group: "监测站点",
    colorHex: "#26a69a",
    defaultVisible: false,
    kind: "monitorPoint",
    file: "engineering.json",
    icon: "闸门",
    iconSize: 42,
  },
  /** 供水管径分层配色（与业务「管径筛选」图例一致） */
  {
    key: "supplyPipe50",
    name: "DN50–DN100",
    group: "供水管网",
    colorHex: "#1E88E5",
    defaultVisible: false,
    kind: "geoPipe",
    file: "supply-pipe-50-100.json",
    stroke: "#1E88E5",
    width: 1.5,
    diamField: "gj",
    diamLabel: "DN50–DN100",
  },
  {
    key: "supplyPipe100",
    name: "DN100–DN200",
    group: "供水管网",
    colorHex: "#FF9800",
    defaultVisible: false,
    kind: "geoPipe",
    file: "supply-pipe-100-200.json",
    stroke: "#FF9800",
    width: 2,
    diamField: "gj",
    diamLabel: "DN100–DN200",
  },
  {
    key: "supplyPipe200",
    name: "DN200–DN300",
    group: "供水管网",
    colorHex: "#C0CA33",
    defaultVisible: false,
    kind: "geoPipe",
    file: "supply-pipe-200-300.json",
    stroke: "#C0CA33",
    width: 2.2,
    diamField: "gj",
    diamLabel: "DN200–DN300",
  },
  {
    key: "supplyPipe300",
    name: "DN300–DN400",
    group: "供水管网",
    colorHex: "#E53935",
    defaultVisible: false,
    kind: "geoPipe",
    file: "supply-pipe-300-400.json",
    stroke: "#E53935",
    width: 2.5,
    diamField: "gj",
    diamLabel: "DN300–DN400",
  },
  {
    key: "supplyPipe400",
    name: "≥DN400",
    group: "供水管网",
    colorHex: "#EC407A",
    defaultVisible: true,
    kind: "geoPipe",
    file: "supply-pipe-gt400.json",
    stroke: "#EC407A",
    width: 3,
    diamField: "gj",
    diamLabel: "≥DN400管段",
  },
  {
    key: "supplyHydrant",
    name: "消防栓",
    group: "供水管网",
    colorHex: "#ef5350",
    defaultVisible: false,
    kind: "geoPoint",
    file: "supply-hydrant.json",
    icon: "穿墙泵",
    iconSize: 42,
    maxCount: 2000,
    clusterPixelRange: 80,
    clusterMinSize: 4,
  },
  /** 排水管径分层（按接口分档文件；≥400 档用 gj 字段；默认全选） */
  {
    key: "drainPipe200",
    name: "DN0–DN200",
    group: "排水管网",
    colorHex: "#26A69A",
    defaultVisible: true,
    kind: "geoPipe",
    file: "drain-pipe-0-200.json",
    stroke: "#26A69A",
    width: 2,
    diamField: "diameter",
    diamLabel: "DN0–DN200",
  },
  {
    key: "drainPipe400",
    name: "DN200–DN400",
    group: "排水管网",
    colorHex: "#FF9800",
    defaultVisible: true,
    kind: "geoPipe",
    file: "drain-pipe-200-400.json",
    stroke: "#FF9800",
    width: 2.5,
    diamField: "diameter",
    diamLabel: "DN200–DN400",
  },
  {
    key: "drainPipe600",
    name: "DN400–DN600",
    group: "排水管网",
    colorHex: "#C0CA33",
    defaultVisible: true,
    kind: "geoPipe",
    file: "drain-pipe-400-600.json",
    stroke: "#C0CA33",
    width: 2.8,
    diamField: "gj",
    diamMin: 400,
    diamMax: 600,
    diamLabel: "DN400–DN600",
  },
  {
    key: "drainPipe800",
    name: "DN600–DN800",
    group: "排水管网",
    colorHex: "#E53935",
    defaultVisible: true,
    kind: "geoPipe",
    file: "drain-pipe-600-800.json",
    stroke: "#E53935",
    width: 3,
    diamField: "gj",
    diamMin: 600,
    diamMax: 800,
    diamLabel: "DN600–DN800",
  },
  {
    key: "drainPipe1000",
    name: "DN800–DN1000",
    group: "排水管网",
    colorHex: "#EC407A",
    defaultVisible: true,
    kind: "geoPipe",
    file: "drain-pipe-800-1000.json",
    stroke: "#EC407A",
    width: 3.2,
    diamField: "gj",
    diamMin: 800,
    diamMax: 1000,
    diamLabel: "DN800–DN1000",
  },
  {
    key: "drainPipe1200",
    name: "DN1000–DN1200",
    group: "排水管网",
    colorHex: "#AB47BC",
    defaultVisible: true,
    kind: "geoPipe",
    file: "drain-pipe-1000-1200.json",
    stroke: "#AB47BC",
    width: 3.4,
    diamField: "gj",
    diamMin: 1000,
    diamMax: 1200,
    diamLabel: "DN1000–DN1200",
  },
  {
    key: "drainPipe1400",
    name: "DN1200–DN1400",
    group: "排水管网",
    colorHex: "#7E57C2",
    defaultVisible: true,
    kind: "geoPipe",
    file: "drain-pipe-1200-1400.json",
    stroke: "#7E57C2",
    width: 3.6,
    diamField: "gj",
    diamMin: 1200,
    diamMax: 1400,
    diamLabel: "DN1200–DN1400",
  },
  {
    key: "drainPipe2000",
    name: "DN1400–DN2000",
    group: "排水管网",
    colorHex: "#5C6BC0",
    defaultVisible: true,
    kind: "geoPipe",
    file: "drain-pipe-1400-2000.json",
    stroke: "#5C6BC0",
    width: 3.8,
    diamField: "gj",
    diamMin: 1400,
    diamMax: 2000,
    diamLabel: "DN1400–DN2000",
  },
  {
    key: "drainManhole",
    name: "检修井",
    group: "排水管网",
    colorHex: "#bcaaa4",
    defaultVisible: false,
    kind: "geoPoint",
    file: "drain-manhole.json",
    icon: "液位计",
    iconSize: 42,
    maxCount: 3000,
    clusterPixelRange: 80,
    clusterMinSize: 5,
  },
];

function transformRing(coords, fromCrs = DATA_CRS) {
  return coords.map(([lng, lat]) => {
    const w = toWgs(lng, lat, fromCrs);
    if (!w) return [lng, lat];
    return [w.lng, w.lat];
  });
}

function transformGeometry(geom, fromCrs = DATA_CRS) {
  if (!geom || !geom.coordinates) return geom;
  const type = geom.type;
  if (type === "Point") {
    const [lng, lat] = geom.coordinates;
    const w = toWgs(lng, lat, fromCrs);
    if (!w) return geom;
    return { type, coordinates: [w.lng, w.lat] };
  }
  if (type === "LineString") {
    return { type, coordinates: transformRing(geom.coordinates, fromCrs) };
  }
  if (type === "MultiLineString") {
    return {
      type,
      coordinates: geom.coordinates.map((line) =>
        transformRing(line, fromCrs)
      ),
    };
  }
  if (type === "Polygon") {
    return {
      type,
      coordinates: geom.coordinates.map((ring) =>
        transformRing(ring, fromCrs)
      ),
    };
  }
  return geom;
}

/**
 * 供水管线 properties 中 qdjd/qdwd/zdjd/zdwd 更接近测绘 WGS/CGCS2000，
 * 优先用其重建 LineString，避免 GCJ geometry 与底图偏差。
 */
function supplyLineFromWgsProps(props) {
  if (!props) return null;
  const qdjd = Number(props.qdjd);
  const qdwd = Number(props.qdwd);
  const zdjd = Number(props.zdjd);
  const zdwd = Number(props.zdwd);
  if (![qdjd, qdwd, zdjd, zdwd].every(Number.isFinite)) return null;
  // 明显不在相城范围则放弃
  if (qdjd < 118 || qdjd > 123 || qdwd < 30 || qdwd > 33) return null;
  return {
    type: "LineString",
    coordinates: [
      [qdjd, qdwd],
      [zdjd, zdwd],
    ],
  };
}

/** 管径是否落在 [diamMin, diamMax)；边界未设则不过滤 */
function diamInRange(props, cfg) {
  if (cfg?.diamMin == null && cfg?.diamMax == null) return true;
  const field = cfg.diamField || "gj";
  const n = Number(props?.[field]);
  if (!Number.isFinite(n)) return false;
  if (cfg.diamMin != null && n < cfg.diamMin) return false;
  if (cfg.diamMax != null && n >= cfg.diamMax) return false;
  return true;
}

/** Pattern A → FeatureCollection（统一转 WGS84） */
function normalizeFeatureCollection(payload, maxCount, cfg) {
  const raw = payload?.data?.features || payload?.features || [];
  const features = [];
  for (let i = 0; i < raw.length; i++) {
    if (maxCount != null && features.length >= maxCount) break;
    const f = raw[i];
    if (!f?.geometry) continue;
    const props = { ...(f.properties || {}) };
    if (f.type && f.type !== "Feature") {
      props.featureType = f.type;
    }
    if (!diamInRange(props, cfg)) continue;

    // 供水管：优先测绘起终点（已是 WGS 系），否则 GCJ geometry 转换
    const wgsLine = supplyLineFromWgsProps(props);
    let geometry;
    if (wgsLine && f.geometry?.type === "LineString") {
      geometry = wgsLine;
    } else {
      geometry = transformGeometry(f.geometry, DATA_CRS);
    }

    features.push({
      type: "Feature",
      geometry,
      properties: props,
    });
  }
  return { type: "FeatureCollection", features };
}

/** 圩区高对比色板（深色底图上更醒目） */
const POLDER_PALETTE = [
  "#00E5FF",
  "#FFD600",
  "#FF4081",
  "#76FF03",
  "#FF9100",
  "#E040FB",
  "#40C4FF",
  "#FF6E40",
];

function vividPolderFill(fillStr, index, fallbackHex) {
  const paletteHex = POLDER_PALETTE[index % POLDER_PALETTE.length];
  const vivid = Cesium.Color.fromCssColorString(paletteHex);
  if (!fillStr) {
    return vivid.withAlpha(0.55);
  }
  const raw = rgbaToColor(fillStr, fallbackHex || paletteHex);
  // 原色偏浅，向高饱和色板靠拢并提高不透明度
  const mixed = Cesium.Color.lerp(raw, vivid, 0.65, new Cesium.Color());
  mixed.alpha = 0.55;
  return mixed;
}

function vividPolderStroke(borderStr, index, fallbackHex) {
  const paletteHex = POLDER_PALETTE[index % POLDER_PALETTE.length];
  const vivid = Cesium.Color.fromCssColorString(paletteHex);
  if (!borderStr) {
    return vivid.withAlpha(0.95);
  }
  const raw = rgbaToColor(borderStr, fallbackHex || paletteHex);
  const mixed = Cesium.Color.lerp(raw, vivid, 0.55, new Cesium.Color());
  mixed.alpha = 0.95;
  return mixed;
}

/** 圩区略抬离椭球面，减少与 globe 深度冲突 */
const POLDER_HEIGHT = 28;

/**
 * 总览/水雨情：相机高度低于该值（贴近底图）时隐藏圩区，只保留站点点位；
 * 拉高后再显示圩区边界。
 */
export const POLDER_LOD_MIN_HEIGHT = 9000;

function raiseRingPositions(positions, height = POLDER_HEIGHT) {
  if (!positions?.length) return positions;
  const out = [];
  for (let i = 0; i < positions.length; i++) {
    const c = Cesium.Cartographic.fromCartesian(positions[i]);
    if (!Cesium.defined(c)) continue;
    out.push(
      Cesium.Cartesian3.fromRadians(c.longitude, c.latitude, height)
    );
  }
  return out;
}

async function loadPolder(viewer, cfg) {
  const json = await fetchJson(cfg.file);
  const list = json?.data?.list || [];
  const features = [];
  let sampleLogged = false;

  for (const item of list) {
    const ring = parseLonLatChain(item.poldRange);
    if (ring.length < 3) continue;

    const coords = [];
    for (const p of ring) {
      // 圩区 poldRange 为 GCJ-02，必须转 WGS84 才能贴合 Cesium 底图
      const w = toWgs(p.lng, p.lat, "gcj02");
      if (!w) continue;
      if (!sampleLogged) {
        console.log("[xc][圩区] GCJ→WGS", item.poldName || item.poldCode, {
          gcj: [p.lng, p.lat],
          wgs: [w.lng, w.lat],
        });
        sampleLogged = true;
      }
      coords.push([w.lng, w.lat]);
    }
    if (coords.length < 3) continue;

    // GeoJSON 多边形环需闭合
    const [lng0, lat0] = coords[0];
    const [lngN, latN] = coords[coords.length - 1];
    if (lng0 !== lngN || lat0 !== latN) {
      coords.push([lng0, lat0]);
    }

    // 中心点：优先接口 centerLonLat，否则环平均
    let centerW = null;
    const cLl = parseLonLat(item.centerLonLat);
    if (cLl) centerW = toWgs(cLl.lng, cLl.lat, "gcj02");
    if (!centerW) {
      const n = coords.length - 1;
      let sx = 0;
      let sy = 0;
      for (let i = 0; i < n; i++) {
        sx += coords[i][0];
        sy += coords[i][1];
      }
      centerW = { lng: sx / n, lat: sy / n };
    }

    features.push({
      type: "Feature",
      properties: {
        name: item.poldName || item.poldCode || "圩区",
        名称: item.poldName,
        编码: item.poldCode,
        面积: item.poldArea != null && item.poldArea !== "" ? `${item.poldArea} km²` : "",
        位置: item.poldLoc || "",
        行政区: item.addvnm || "",
        分类: item.poldClas || "",
        运行状态: item.runStat || "",
        工程状态: item.engStat || "",
        涉及村居: item.note || "",
        堤长: item.dikeLen != null && item.dikeLen !== "" ? `${item.dikeLen} km` : "",
        堤顶高程: item.dikeTopEl != null && item.dikeTopEl !== "" ? `${item.dikeTopEl} m` : "",
        堤顶宽: item.topWidMin != null && item.topWidMin !== "" ? `${item.topWidMin} m` : "",
        设计流量: item.desFlow != null && item.desFlow !== "" ? `${item.desFlow} m³/s` : "",
        排涝流量: item.poldFl != null && item.poldFl !== "" ? `${item.poldFl} m³/s` : "",
        排涝动力: item.poldPow != null && item.poldPow !== "" ? `${item.poldPow} kW` : "",
        泵站数量: item.poldNum != null && item.poldNum !== "" ? String(item.poldNum) : "",
        水闸数量: item.wasNum != null && item.wasNum !== "" ? String(item.wasNum) : "",
        运管单位: item.opPr || "",
        开工日期: item.startDate || "",
        完工日期: item.compDate || "",
        更新日期: item.updateDate || "",
        描述: item.poldDesc || "",
        fillColor: item.fillColor || "",
        borderColor: item.borderColor || "",
        centerLng: centerW.lng,
        centerLat: centerW.lat,
      },
      geometry: {
        type: "Polygon",
        coordinates: [coords],
      },
    });
  }

  const fc = { type: "FeatureCollection", features };
  const fillDefault = Cesium.Color.fromCssColorString(cfg.colorHex).withAlpha(
    0.55
  );
  const strokeDefault = Cesium.Color.fromCssColorString(cfg.colorHex).withAlpha(
    0.95
  );

  // 不用 clampToGround GroundPrimitive：半透明贴地多边形经常拾取失败
  const dataSource = await Cesium.GeoJsonDataSource.load(fc, {
    clampToGround: false,
    fill: fillDefault,
    stroke: strokeDefault,
    strokeWidth: 3,
  });
  dataSource.name = cfg.name;

  const entities = dataSource.entities.values;
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    const fillStr = e.properties?.fillColor?.getValue?.();
    const borderStr = e.properties?.borderColor?.getValue?.();
    const name =
      e.properties?.name?.getValue?.() || e.properties?.名称?.getValue?.();
    if (name) e.name = String(name);

    const centerLng = Number(e.properties?.centerLng?.getValue?.());
    const centerLat = Number(e.properties?.centerLat?.getValue?.());
    if (Number.isFinite(centerLng) && Number.isFinite(centerLat)) {
      // 与圩区面同高，保证屏幕投影稳定；position 赋给 Entity 后会变成 Property，不能再 .clone()
      const center = Cesium.Cartesian3.fromDegrees(
        centerLng,
        centerLat,
        POLDER_HEIGHT
      );
      e.position = center;
      e._xcCenter = Cesium.Cartesian3.clone(center);
    }

    // 多边形 Entity 不需要点/图标，避免近景误看成「只剩点位」
    e.point = undefined;
    e.billboard = undefined;
    e.label = undefined;

    if (e.polygon) {
      const fill = vividPolderFill(fillStr, i, cfg.colorHex);
      const border = vividPolderStroke(borderStr, i, cfg.colorHex);
      e.polygon.material = fill;
      e.polygon.outline = true;
      e.polygon.outlineColor = border;
      e.polygon.outlineWidth = 3;
      e.polygon.height = POLDER_HEIGHT;
      e.polygon.extrudedHeight = undefined;
      e.polygon.perPositionHeight = false;
      // 椭球地形下勿 CLAMP_TO_GROUND，避免缩放时贴地几何反复重建 VAO
      e.polygon.heightReference = Cesium.HeightReference.NONE;
      e.polygon.classificationType = undefined;

      e._xcPolder = true;
      e._xcPolderOutline = false;
      e._xcFillBase = fill.clone();
      e._xcStrokeBase = border.clone();
      e._xcFillHoverAlpha = 0.85;
      e._xcPolderName = String(name || "圩区");

      // 虚线边界（水雨情页启用；首页仍用面填充+实线描边）
      const hierarchy = e.polygon.hierarchy?.getValue?.(Cesium.JulianDate.now());
      const ring = hierarchy?.positions;
      if (ring && ring.length >= 2) {
        const raised = raiseRingPositions(ring, POLDER_HEIGHT);
        e.polyline = new Cesium.PolylineGraphics({
          positions: raised,
          width: 2,
          clampToGround: false,
          show: false,
          material: new Cesium.PolylineDashMaterialProperty({
            color: border,
            dashLength: 16,
          }),
        });
      }
    }
  }

  await viewer.dataSources.add(dataSource);
  console.log(`[xc] 圩区 ${features.length} 个（高对比色）`);
  return dataSource;
}

/** 圩区显示模式：fill 填充实线（首页） / outline 无填充虚线（水雨情） */
export function applyPolderDisplayMode(dataSource, mode = "fill") {
  if (!dataSource?.entities) return;
  const outline = mode === "outline";
  dataSource._xcPolderMode = outline ? "outline" : "fill";
  const lodOk = dataSource._xcPolderLodVisible !== false;
  const entities = dataSource.entities.values;
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    if (!e._xcPolder || !e.polygon) continue;
    e._xcPolderOutline = outline;
    if (outline) {
      // 极低透明仍可拾取悬停
      e.polygon.material = Cesium.Color.WHITE.withAlpha(0.02);
      e.polygon.outline = false;
      e.polygon.height = POLDER_HEIGHT;
      e.polygon.show = lodOk;
      if (e.polyline) {
        e.polyline.show = lodOk;
        e.polyline.width = 2;
      }
    } else {
      if (e._xcFillBase) e.polygon.material = e._xcFillBase.clone();
      e.polygon.outline = true;
      e.polygon.height = POLDER_HEIGHT;
      e.polygon.show = lodOk;
      if (e._xcStrokeBase) e.polygon.outlineColor = e._xcStrokeBase.clone();
      e.polygon.outlineWidth = 3;
      if (e.polyline) e.polyline.show = false;
    }
  }
}

/**
 * 按相机高度控制圩区显隐。
 * @param enabled 总览/水雨情开启：近景隐藏、远景显示；其它页始终显示
 */
export function applyPolderCameraLod(dataSource, cameraHeight, enabled = true) {
  if (!dataSource?.entities) return;
  const show = !enabled || cameraHeight >= POLDER_LOD_MIN_HEIGHT;
  dataSource._xcPolderLodVisible = show;
  const outline = dataSource._xcPolderMode === "outline";
  const entities = dataSource.entities.values;
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    if (!e._xcPolder) continue;
    if (e.polygon) e.polygon.show = show;
    if (e.polyline) e.polyline.show = show && outline;
  }
}

async function loadDike(viewer, cfg) {
  const json = await fetchJson(cfg.file);
  const list = json?.data?.list || [];
  const features = [];

  for (const item of list) {
    const pts = parseLonLatChain(item.dikeGeom);
    if (pts.length < 2) continue;
    const coords = [];
    for (const p of pts) {
      const w = toWgs(p.lng, p.lat, "gcj02");
      if (!w) continue;
      coords.push([w.lng, w.lat]);
    }
    if (coords.length < 2) continue;

    features.push({
      type: "Feature",
      properties: {
        name: item.dikeName || item.dikeCode || "堤防",
        名称: item.dikeName,
        编码: item.dikeCode,
        长度: item.dikeLength,
        等级: item.dikeGrad,
        类型: item.dikeType,
      },
      geometry: {
        type: "LineString",
        coordinates: coords,
      },
    });
  }

  const color = Cesium.Color.fromCssColorString(cfg.colorHex).withAlpha(0.75);
  const dataSource = await Cesium.GeoJsonDataSource.load(
    { type: "FeatureCollection", features },
    {
      // 当前为椭球地形，贴地线会走 GroundPolyline 半透明通道，缩放易 VAO 销毁
      clampToGround: false,
      stroke: color,
      strokeWidth: 4,
    }
  );
  dataSource.name = cfg.name;

  const entities = dataSource.entities.values;
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    const name =
      e.properties?.name?.getValue?.() || e.properties?.名称?.getValue?.();
    if (name) e.name = String(name);
    if (e.polyline) {
      e.polyline.material = color;
      e.polyline.width = 4;
      e.polyline.clampToGround = false;
      e._xcDike = true;
      e._xcStrokeBase = color.clone();
      e._xcStrokeHoverAlpha = 1;
    }
  }

  await viewer.dataSources.add(dataSource);
  console.log(`[xc] 堤防 ${features.length} 条（GCJ-02→WGS84）`);
  return dataSource;
}

async function loadListPoints(viewer, cfg) {
  const json = await fetchJson(cfg.file);
  const list = json?.data?.list || json?.data || [];
  const ds = new Cesium.CustomDataSource(cfg.name);
  const fallbackColor = Cesium.Color.fromCssColorString(
    cfg.pointColor || cfg.colorHex || "#3de7ff"
  );

  for (const item of list) {
    const raw =
      item[cfg.listGeom] ||
      item.longitude ||
      item.gategeom ||
      item.longLat ||
      item.centerLonLat;
    const ll = parseLonLat(raw);
    if (!ll) continue;
    const w = toWgs(ll.lng, ll.lat);
    if (!w) continue;
    const name = item[cfg.listName] || item.siteName || item.name || cfg.name;
    const iconUrl = resolvePointIconUrl(cfg, item);
    const size = cfg.iconSize || 42;

    const entity = {
      name,
      position: Cesium.Cartesian3.fromDegrees(w.lng, w.lat),
      properties: flattenProps(item, cfg.listName),
    };

    if (iconUrl) {
      entity.billboard = makeBillboardOptions(iconUrl, { size });
    } else {
      entity.point = {
        pixelSize: cfg.pixelSize || 14,
        color: fallbackColor,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.7),
        outlineWidth: 1,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      };
    }

    if (cfg.showLabel !== false) {
      entity.label = makePointLabelOptions(name, {
        // 图标 PNG 有透明边，略收紧间距
        offsetY: iconUrl ? -(size - 10) : -10,
      });
    }

    ds.entities.add(entity);
  }

  await viewer.dataSources.add(ds);

  // 仅显式 cfg.cluster === true 时开启（默认关闭，避免缩放崩溃）
  if (cfg.cluster === true) {
    enablePointClustering(ds, {
      pixelRange: cfg.clusterPixelRange ?? 56,
      minimumClusterSize: cfg.clusterMinSize ?? 3,
    });
  }

  return ds;
}

async function loadMonitorPoints(viewer, cfg) {
  const json = await fetchJson(cfg.file);
  const list = Array.isArray(json?.data) ? json.data : json?.data?.list || [];
  const ds = new Cesium.CustomDataSource(cfg.name);
  const fallbackColor = Cesium.Color.fromCssColorString(
    cfg.pointColor || cfg.colorHex || "#3de7ff"
  );

  for (const item of list) {
    const ll = parseLonLat(item.longitude || item.longLat);
    if (!ll) continue;
    const w = toWgs(ll.lng, ll.lat);
    if (!w) continue;
    const warn =
      item.isWarn === true || item.isWarn === 1 || item.isWarn === "1";
    const name = item.siteName || item.pointName || cfg.name;
    const iconUrl = resolvePointIconUrl(cfg, item);
    const size = cfg.iconSize || 42;

    const entity = {
      name,
      position: Cesium.Cartesian3.fromDegrees(w.lng, w.lat),
      properties: cleanProps({
        名称: name,
        编码: item.siteCode || item.pointId,
        类型: item.monitorType || item.dataType,
        告警: warn ? "是" : "否",
        联网:
          item.networkingStatus === "1" || item.networkingStatus === 1
            ? "在线"
            : item.networkingStatus != null
              ? String(item.networkingStatus)
              : undefined,
        行政区: item.addvnm,
        地址: item.siteAddress,
        水位: item.dwz ?? item.upz,
        雨量: item.drp,
        警戒: item.wrz,
        开闸:
          item.openGate != null
            ? `${item.openGate}/${item.totalGate ?? "-"}`
            : undefined,
        开泵: item.openPump,
        时间: item.datetime,
      }),
    };

    if (iconUrl) {
      entity.billboard = makeBillboardOptions(iconUrl, { warn, size });
    } else {
      entity.point = {
        pixelSize: warn ? 16 : 12,
        color: warn
          ? Cesium.Color.fromCssColorString("#ff5252")
          : fallbackColor,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.8),
        outlineWidth: 1,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      };
    }

    entity.label = makePointLabelOptions(name, {
      offsetY: iconUrl ? -(size - 10) : -10,
      far: 12000,
    });

    ds.entities.add(entity);
  }

  await viewer.dataSources.add(ds);

  if (cfg.cluster === true) {
    enablePointClustering(ds, {
      pixelRange: cfg.clusterPixelRange ?? 56,
      minimumClusterSize: cfg.clusterMinSize ?? 3,
    });
  }

  return ds;
}

function cleanProps(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (v == null || v === "") continue;
    out[k] = v;
  }
  return out;
}

function flattenProps(item, nameKey) {
  const out = {};
  if (nameKey && item[nameKey] != null) out.名称 = item[nameKey];
  const prefer = [
    ["gatename", "名称"],
    ["cbdname", "名称"],
    ["name", "名称"],
    ["rname", "名称"],
    ["gatetype", "类型"],
    ["gatecode", "编码"],
    ["poldCode", "编码"],
    ["poldName", "名称"],
    ["poldArea", "面积"],
    ["addvnm", "行政区"],
    ["caddress", "地址"],
    ["address", "地址"],
    ["raddress", "地址"],
    ["siteAddress", "地址"],
    ["ctel", "电话"],
    ["cleader", "负责人"],
    ["peoples", "人数"],
    ["remployee", "人数"],
    ["pumpNum", "泵台数"],
    ["drainFlow", "排涝流量"],
    ["engStat", "工程状态"],
    ["runStat", "运行状态"],
    ["mapLegendSign", "图例"],
  ];
  for (const [k, label] of prefer) {
    if (item[k] == null || String(item[k]).trim() === "") continue;
    if (label === "名称" && out.名称) continue;
    out[label] = item[k];
  }
  return out;
}

async function loadGeoPipe(viewer, cfg) {
  const t0 = performance.now();
  const json = await fetchJson(cfg.file);
  const fc = normalizeFeatureCollection(json, cfg.maxCount, cfg);
  console.log(
    `[xc] ${cfg.name}: ${fc.features.length} 段 (${(performance.now() - t0).toFixed(0)}ms fetch+transform)`
  );

  const stroke = Cesium.Color.fromCssColorString(cfg.stroke || cfg.colorHex);
  const dataSource = await Cesium.GeoJsonDataSource.load(fc, {
    clampToGround: false,
    stroke,
    strokeWidth: cfg.width || 2,
  });
  dataSource.name = cfg.name;

  const entities = dataSource.entities.values;
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    if (e.polyline) {
      e.polyline.material = stroke;
      e.polyline.width = cfg.width || 2;
      e.polyline.clampToGround = false;
    }
    const props = e.properties;
    if (props) {
      const diam = props[cfg.diamField]?.getValue?.();
      if (diam != null) {
        e.name = `${cfg.name} DN${diam}`;
      }
    }
  }

  await viewer.dataSources.add(dataSource);
  return dataSource;
}

async function loadGeoPoint(viewer, cfg) {
  const t0 = performance.now();
  const json = await fetchJson(cfg.file);
  const fc = normalizeFeatureCollection(json, cfg.maxCount);
  console.log(
    `[xc] ${cfg.name}: ${fc.features.length} 点 (${(performance.now() - t0).toFixed(0)}ms)`
  );

  const color = Cesium.Color.fromCssColorString(
    cfg.pointColor || cfg.colorHex || "#3de7ff"
  );
  const dataSource = await Cesium.GeoJsonDataSource.load(fc, {
    clampToGround: false,
    markerSize: 0,
  });
  dataSource.name = cfg.name;

  const iconUrl = resolvePointIconUrl(cfg);
  const size = cfg.iconSize || 42;
  const entities = dataSource.entities.values;
  for (let i = 0; i < entities.length; i++) {
    const e = entities[i];
    e.billboard = undefined;
    e.point = undefined;
    if (iconUrl) {
      e.billboard = new Cesium.BillboardGraphics(
        makeBillboardOptions(iconUrl, { size })
      );
    } else {
      e.point = new Cesium.PointGraphics({
        pixelSize: cfg.pixelSize || 5,
        color,
        outlineColor: Cesium.Color.WHITE.withAlpha(0.5),
        outlineWidth: 1,
        heightReference: Cesium.HeightReference.NONE,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      });
    }
    const pno = e.properties?.pno?.getValue?.();
    const ft = e.properties?.featureType?.getValue?.();
    const addr = e.properties?.address?.getValue?.();
    // 标题优先点号，避免只显示中文类型名（如「检修井」）
    if (pno || addr || ft) e.name = pno || addr || ft || cfg.name;
  }

  await viewer.dataSources.add(dataSource);

  if (cfg.cluster === true) {
    enablePointClustering(dataSource, {
      pixelRange: cfg.clusterPixelRange ?? 72,
      minimumClusterSize: cfg.clusterMinSize ?? 4,
    });
  }

  return dataSource;
}

async function loadOne(viewer, cfg) {
  switch (cfg.kind) {
    case "polder":
      return loadPolder(viewer, cfg);
    case "dike":
      return loadDike(viewer, cfg);
    case "listPoint":
      return loadListPoints(viewer, cfg);
    case "monitorPoint":
      return loadMonitorPoints(viewer, cfg);
    case "geoPipe":
      return loadGeoPipe(viewer, cfg);
    case "geoPoint":
      return loadGeoPoint(viewer, cfg);
    default:
      throw new Error(`未知图层类型: ${cfg.kind}`);
  }
}

/**
 * 加载图层。keys 为空则按 defaultVisible 加载。
 * @returns {{ key, name, group, colorHex, visible, dataSource }[]}
 */
export async function loadWaterLayers(viewer, keys, onProgress) {
  const targets = keys?.length
    ? LAYER_DEFS.filter((d) => keys.includes(d.key))
    : LAYER_DEFS.filter((d) => d.defaultVisible);

  const results = [];
  for (let i = 0; i < targets.length; i++) {
    const cfg = targets[i];
    onProgress?.(
      Math.round(((i + 0.2) / targets.length) * 100),
      `加载 ${cfg.name}…`
    );
    try {
      const t0 = performance.now();
      const dataSource = await loadOne(viewer, cfg);
      dataSource.show = true;
      console.log(
        `[xc] ✓ ${cfg.name} ${(performance.now() - t0).toFixed(0)}ms`
      );
      results.push({
        key: cfg.key,
        name: cfg.name,
        group: cfg.group,
        colorHex: cfg.colorHex,
        visible: true,
        dataSource: markRaw(dataSource),
        dataVersion: LAYER_DATA_VERSION,
        def: cfg,
      });
    } catch (err) {
      console.warn(`[xc] ✗ ${cfg.name}`, err);
    }
  }
  return results;
}

/** 按需加载尚未加载的图层定义 */
export async function loadLayerByKey(viewer, key) {
  const cfg = LAYER_DEFS.find((d) => d.key === key);
  if (!cfg) throw new Error(`未知图层 ${key}`);
  const dataSource = await loadOne(viewer, cfg);
  return {
    key: cfg.key,
    name: cfg.name,
    group: cfg.group,
    colorHex: cfg.colorHex,
    visible: true,
    dataSource: markRaw(dataSource),
    dataVersion: LAYER_DATA_VERSION,
    def: cfg,
  };
}

/** 从列表项 / 告警项取出原始 "lng,lat" 字符串（业务坐标，未转换） */
export function pickLonLatString(item) {
  if (!item) return null;
  const raw = item.raw && typeof item.raw === "object" ? item.raw : item;
  return (
    item.longitude ||
    item.longLat ||
    item.gategeom ||
    raw.longitude ||
    raw.longLat ||
    raw.gategeom ||
    raw.centerLonLat ||
    null
  );
}

/** 列表项 / 告警项 → Cesium 可用的 WGS84 度坐标 */
export function itemToMapDegrees(item, fromCrs = DATA_CRS) {
  const ll = parseLonLat(pickLonLatString(item));
  if (!ll) return null;
  return toCesiumDegrees(ll.lng, ll.lat, fromCrs);
}

